import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { AnnotationStatus } from '@rossum/api-client/shared';
import { snakeToCamel } from '@rossum/api-client/utils';
import StatusChip from '@rossum/components/status/StatusChip';
import { isStatusVisible } from '../../features/document-list-columns/status/types';
import { State } from '../../types/state';

type DocumentStatusProps = {
  status: AnnotationStatus | undefined;
  modifier:
    | {
        id: number;
        username: string;
      }
    | string
    | undefined;
  workflowStepName?: string;
  disabled?: boolean;
  dataCy?: string;
};

export const DocumentStatus = ({
  status,
  modifier,
  workflowStepName,
  disabled,
  dataCy,
}: DocumentStatusProps) => {
  const intl = useIntl();
  const userId = useSelector((state: State) => state.user.id);

  const isValidModifier = typeof modifier !== 'string';

  const isOwned = isValidModifier ? modifier?.id === userId : false;

  const enforceVisible = status === 'split';

  const getTooltipTitle = () => {
    if (status === 'reviewing') {
      return intl.formatMessage(
        {
          id: `containers.annotationList.statuses.reviewing.tooltip`,
        },
        {
          modifier:
            (isValidModifier && modifier?.username) ??
            intl.formatMessage({
              id: 'containers.documents.unknownModifier',
            }),
        }
      );
    }

    if (status === 'in_workflow') {
      return workflowStepName ?? null;
    }

    return null;
  };

  if (status === undefined || (!enforceVisible && !isStatusVisible(status))) {
    return null;
  }

  return (
    <StatusChip
      status={snakeToCamel(status)}
      isOwned={isOwned}
      tooltipTitle={getTooltipTitle()}
      disabled={disabled}
      dataCy={dataCy}
    />
  );
};
